import React from "react";
import globalStyles from "./../../../assets/styles/base/globalPage.module.scss";
import styles from "./CreateShipping.module.scss";
import primaryComponents from "../../primaryComponents";
import useShippingForm from "./useShippingForm";
import Validation from "./Validation";
import LoaderHelper from "../LoaderHelper/LoaderHelper";
import { MinusCircleIcon, PlusCircle } from "../../../assets/svgIcons";
import Modal from "../Modal/Modal";
import UpdateCityModal from "../UpdateCityModal";

const PricingRuleForm = ({ rule, label, onRemove, onUpdate, currency }) => {
  return (
    <div className={styles.pricingRuleCard}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="small-text-size font-weight-semibold purple--text">
          {label}
        </span>
        <button
          type="button"
          className={styles.removeRuleBtn}
          onClick={onRemove}
        >
          <MinusCircleIcon width={14} height={14} />
          Remove
        </button>
      </div>

      <div className={styles.pricingRuleGrid}>
        <div>
          <label className={styles.pricingRuleLabel}>
            Rate{currency ? ` (${currency})` : ""}
          </label>
          <input
            type="number"
            min={0}
            className={styles.ruleInput}
            placeholder="Enter rate"
            value={rule?.rate || ""}
            onChange={(e) => onUpdate("rate", e.target.value)}
          />
        </div>

        <div>
          <label className={styles.pricingRuleLabel}>
            Delivery Window (days)
          </label>
          <input
            type="number"
            min={1}
            className={styles.ruleInput}
            placeholder="e.g. 5"
            value={rule?.delivery_window || ""}
            onChange={(e) => onUpdate("delivery_window", e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

const CreateShippingForm = ({
  closeWidget,
  refetch,
  isEditMode,
  shippingInfo,
}) => {
  const {
    shippingDetails,
    selectInputRef,
    selectedState,
    countryDetails,
    isLoadingCountryDetails,
    isLoadingCountries,
    isLoadingMenus,
    cities,
    isAddingShipping,
    isEditingShipping,
    countries,
    menus,
    selectedCountry,
    setShippingDetails,
    setCities,
    setSelectedState,
    handleAddShipping,
    handleEditShipping,
    setSelectedCountry,
    addCity,
    setCurrency,
    currency,
    addCityModal,
    setAddCityModal,
    handleAddCityToLocation,
    getPricingRule,
    upsertPricingRule,
    updatePricingRule,
    removePricingRule,
  } = useShippingForm({
    shippingInfo,
    isEditMode,
    refetch,
    closeWidget,
  });
  const {
    handleSubmit,
    control,
    register,
    trigger,
    clearErrors,
    formState: { errors },
  } = Validation({ shippingDetails, shippingInfo });

  const getCityName = (stateId, cityId) =>
    countryDetails?.states
      ?.find((s) => s.id === stateId)
      ?.cities?.find((c) => c.id === cityId)?.name || cityId;

  return (
    <div className={`${globalStyles.pagePaddings} p-4`}>
      <div className={globalStyles.pageColumnGaps}>
        <div className={globalStyles.pageColumnGaps}>
          <div className="mt-3">
            <div className={styles.location}>
              <div className="w-100 cover">
                <primaryComponents.InputFieldV2
                  control={control}
                  classNames="white"
                  name="shippingName"
                  label="Zone Name"
                  placeholder="E.g Country, state or city name - Group 1"
                  isRequired={true}
                  value={shippingDetails?.name}
                  onChange={({ target: { value } }) => {
                    setShippingDetails((prev) => ({
                      ...prev,
                      name: value,
                    }));
                  }}
                  errorMessage={errors.shippingName}
                  onKeyUp={() =>
                    errors.shippingName !== undefined && trigger("shippingName")
                  }
                  register={register}
                />
              </div>
              <div className="w-100 cover">
                <LoaderHelper
                  classNames={"mt-4"}
                  isLoading={isLoadingCountries}
                >
                  <primaryComponents.SelectFieldV2
                    control={control}
                    label="Select country"
                    isRequired={true}
                    value={shippingDetails?.countryId || "Select country"}
                    onChange={(value) => {
                      setSelectedCountry(value);
                      setSelectedState([]);
                      selectInputRef.current.clearValue();
                      // Remove any existing pricing rules when country changes
                      setShippingDetails((prev) => ({
                        ...prev,
                        pricing_rules: [],
                      }));
                    }}
                    name="shippingCountryId"
                    defaultValue={
                      shippingDetails?.countryId ||
                      shippingInfo?.country?.id ||
                      ""
                    }
                    options={countries?.map((country) => ({
                      id: country?.id,
                      name: `${country?.emoji} ${country?.name}`,
                    }))}
                    nameKey="name"
                    idKey="id"
                    errorMessage={errors.shippingCountryId}
                    register={register}
                  />
                </LoaderHelper>

                {/* Country-level custom pricing rule */}
                {selectedCountry && (
                  <div className="mt-2">
                    {getPricingRule(selectedCountry, "", "") ? (
                      <PricingRuleForm
                        rule={getPricingRule(selectedCountry, "", "")}
                        label="Custom rate — applies to all states & cities"
                        currency={currency}
                        onRemove={() =>
                          removePricingRule(selectedCountry, "", "")
                        }
                        onUpdate={(field, value) =>
                          updatePricingRule(
                            selectedCountry,
                            "",
                            "",
                            field,
                            value,
                          )
                        }
                      />
                    ) : (
                      <button
                        type="button"
                        className={styles.addRuleBtn}
                        onClick={() =>
                          upsertPricingRule(selectedCountry, "", "")
                        }
                      >
                        <PlusCircle stroke="#7647EE" width={14} height={14} />
                        Customize rate for country
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            <LoaderHelper classNames={"mt-4"} isLoading={isLoadingMenus}>
              <primaryComponents.SelectFieldV2
                control={control}
                label="Select applicable storefront"
                isRequired={true}
                value={
                  shippingDetails?.storefront_ids ||
                  "Select applicable storefront"
                }
                onChange={(selectedValue) => {
                  const selectedMenu = menus?.find(
                    (menu) => menu?.id === selectedValue,
                  );
                  setCurrency(selectedMenu?.store?.currency_code);

                  setShippingDetails((prev) => ({
                    ...prev,
                    storefront_ids: [selectedValue],
                  }));
                }}
                name="shippingMenuIds"
                defaultValue={shippingDetails?.storefront_ids}
                options={menus}
                nameKey="name"
                idKey="id"
                errorMessage={errors.shippingMenuIds}
                register={register}
              />
            </LoaderHelper>
          </div>

          <div>
            <div className="mt-2">
              <div className="mt-2">
                <LoaderHelper
                  classNames={"mt-4"}
                  isLoading={isLoadingCountryDetails}
                >
                  <primaryComponents.MultipleSelectFieldV2
                    control={control}
                    classNames="white"
                    name="shippingStateIds"
                    label="Select State"
                    placeholder="Select State"
                    value={shippingDetails?.stateIds}
                    onFocus={() => {
                      if (!shippingDetails?.countryId) {
                        trigger("shippingCountryId");
                      } else {
                        clearErrors("shippingCountryId");
                      }
                    }}
                    onChange={(values) => {
                      setCities({});
                      setSelectedState(values);
                      const updatedCities = { ...cities };
                      for (const key in updatedCities) {
                        if (
                          !values?.find(
                            (value) => `${value?.value}` === `${key}`,
                          )
                        )
                          delete updatedCities[key];
                      }
                      setCities(updatedCities);
                      // Clean up pricing rules for removed states
                      const removedStateIds = Object.keys(updatedCities).filter(
                        (k) => !values?.find((v) => `${v.value}` === k),
                      );
                      if (removedStateIds.length > 0) {
                        setShippingDetails((prev) => ({
                          ...prev,
                          pricing_rules: prev.pricing_rules?.filter(
                            (r) =>
                              !removedStateIds.includes(`${r.state_id}`) ||
                              r.city_id,
                          ),
                        }));
                      }
                    }}
                    nameKey="name"
                    idKey="id"
                    isRequired={true}
                    options={
                      isLoadingCountryDetails ? [] : countryDetails?.states
                    }
                    defaultValue={shippingDetails?.stateIds}
                    errorMessage={errors.shippingStateIds}
                    register={register}
                    includeSelectAll={true}
                    selectInputRef={selectInputRef}
                    creatableLabel={"All State"}
                  />
                </LoaderHelper>
              </div>
            </div>

            {selectedState?.length > 0 ? (
              <div className={`mt-2 ${globalStyles.cardWithBorder}`}>
                {selectedState?.map((stateData) => (
                  <div key={stateData?.value}>
                    {/* State header + state-level custom rate button */}
                    <div className="d-flex justify-content-between align-items-center">
                      <div
                        className="d-flex justify-content-end"
                        onClick={() => {
                          setAddCityModal(stateData?.value);
                        }}
                      >
                        <p
                          className="smallest-text-size purple--text font-weight-semibold"
                          style={{ cursor: "pointer" }}
                        >
                          Add City
                        </p>
                      </div>

                      {getPricingRule(
                        selectedCountry,
                        `${stateData?.value}`,
                        "",
                      ) ? null : (
                        <button
                          type="button"
                          className={styles.addRuleBtn}
                          onClick={() =>
                            upsertPricingRule(
                              selectedCountry,
                              `${stateData?.value}`,
                              "",
                            )
                          }
                        >
                          <PlusCircle stroke="#7647EE" width={14} height={14} />
                          Customize rate for {stateData?.label}
                        </button>
                      )}
                    </div>

                    {/* State-level pricing rule form */}
                    {getPricingRule(
                      selectedCountry,
                      `${stateData?.value}`,
                      "",
                    ) && (
                      <PricingRuleForm
                        rule={getPricingRule(
                          selectedCountry,
                          `${stateData?.value}`,
                          "",
                        )}
                        label={`Custom rate for ${stateData?.label} — overrides country rate`}
                        currency={currency}
                        onRemove={() =>
                          removePricingRule(
                            selectedCountry,
                            `${stateData?.value}`,
                            "",
                          )
                        }
                        onUpdate={(field, value) =>
                          updatePricingRule(
                            selectedCountry,
                            `${stateData?.value}`,
                            "",
                            field,
                            value,
                          )
                        }
                      />
                    )}

                    <LoaderHelper
                      classNames={"mt-4"}
                      isLoading={isLoadingCountryDetails}
                    >
                      <primaryComponents.MultipleSelectFieldV2
                        name={`shippingCities-${stateData?.label}`}
                        control={control}
                        options={
                          countryDetails?.states?.find(
                            (data) => stateData?.value === data?.id,
                          )?.cities
                        }
                        condition={true}
                        idKey="id"
                        label={`${stateData?.label}`}
                        nameKey="name"
                        placeholder={`Select cities from ${stateData?.label}`}
                        isRequired={false}
                        Creatable={true}
                        value={
                          cities[stateData?.value]?.map((cityId) => {
                            const city = countryDetails?.states
                              ?.find((data) => stateData?.value === data?.id)
                              ?.cities?.find((city) => city?.id === cityId);
                            return { value: city?.id, label: city?.name };
                          }) || []
                        }
                        defaultValue={
                          cities[stateData?.value]?.map((cityId) => {
                            const city = countryDetails?.states
                              ?.find((data) => stateData?.value === data?.id)
                              ?.cities?.find((city) => city?.id === cityId);
                            return { value: city?.id, label: city?.name };
                          }) || []
                        }
                        onChange={(values) => {
                          const cityIds = values?.map((city) => city.value);
                          setCities((prev) => ({
                            ...prev,
                            [stateData?.value]: cityIds,
                          }));
                          // Clean up pricing rules for removed cities
                          const removedCityIds = (
                            cities[stateData?.value] || []
                          ).filter((id) => !cityIds.includes(id));
                          if (removedCityIds.length > 0) {
                            setShippingDetails((prev) => ({
                              ...prev,
                              pricing_rules: prev.pricing_rules?.filter(
                                (r) => !removedCityIds.includes(`${r.city_id}`),
                              ),
                            }));
                          }
                        }}
                        errorMessage={
                          errors[`shippingCities-${stateData?.label}`]
                        }
                        register={register}
                        includeSelectAll={true}
                        onCreateNewOption={handleAddCityToLocation}
                        state_id={stateData?.value}
                        country_id={selectedCountry}
                        creatableLabel={"All Cities"}
                        addCity={addCity}
                      />
                    </LoaderHelper>

                    {/* Per-city custom rate section */}
                    {cities[stateData?.value]?.length > 0 && (
                      <div className={styles.cityRulesSection}>
                        <p className="smallest-text-size grey--text mb-2">
                          City-level rate overrides (optional)
                        </p>
                        {cities[stateData?.value]?.map((cityId) => {
                          const cityName = getCityName(
                            stateData?.value,
                            cityId,
                          );
                          const rule = getPricingRule(
                            selectedCountry,
                            `${stateData?.value}`,
                            `${cityId}`,
                          );
                          return (
                            <div key={cityId} className="mb-2">
                              {rule ? (
                                <PricingRuleForm
                                  rule={rule}
                                  label={`Custom rate for ${cityName} — overrides state & country rate`}
                                  currency={currency}
                                  onRemove={() =>
                                    removePricingRule(
                                      selectedCountry,
                                      `${stateData?.value}`,
                                      `${cityId}`,
                                    )
                                  }
                                  onUpdate={(field, value) =>
                                    updatePricingRule(
                                      selectedCountry,
                                      `${stateData?.value}`,
                                      `${cityId}`,
                                      field,
                                      value,
                                    )
                                  }
                                />
                              ) : (
                                <button
                                  type="button"
                                  className={styles.addRuleBtn}
                                  onClick={() =>
                                    upsertPricingRule(
                                      selectedCountry,
                                      `${stateData?.value}`,
                                      `${cityId}`,
                                    )
                                  }
                                >
                                  <PlusCircle
                                    stroke="#7647EE"
                                    width={14}
                                    height={14}
                                  />
                                  {cityName}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div
                className={`${globalStyles.cardWithBorder} d-flex justify-content-center align-items-center`}
              >
                <p className="default-text-size">
                  Select state(s) above to configure towns/cities
                </p>
              </div>
            )}
          </div>

          {/* Default rates section */}
          <div className="mt-4">
            <h5>Default Rates & Window</h5>
            <p className="small-text-size grey--text mb-3">
              These apply globally unless overridden at country, state, or city
              level above.
            </p>
            <primaryComponents.InputFieldV2
              control={control}
              classNames="white"
              name="shippingRate"
              label={`Rates ${
                shippingDetails?.storefront_ids
                  ? currency
                  : "No storefront selected"
              }`}
              placeholder="Enter rate for this shipping zones"
              isRequired={true}
              value={shippingDetails?.rate || ""}
              onChange={({ target: { value } }) => {
                setShippingDetails((prev) => ({
                  ...prev,
                  rate: value,
                }));
              }}
              errorMessage={errors.shippingRate}
              onKeyUp={() =>
                errors.shippingRate !== undefined && trigger("shippingRate")
              }
              register={register}
            />

            <primaryComponents.EstimatedInput
              label={"Enter Estimated Delivery Duration (in days)"}
              name="shippingDeliveryWindow"
              control={control}
              type="number"
              placeholder="Enter delivery days, e.g., 5"
              isRequired={true}
              isDisabled={false}
              value={shippingDetails?.delivery_window}
              errorMessage={errors.shippingDeliveryWindow}
              onChange={(e) => {
                setShippingDetails((prev) => ({
                  ...prev,
                  delivery_window: e.target.value,
                }));
              }}
              register={register}
              onKeyUp={() =>
                errors.shippingDeliveryWindow !== undefined &&
                trigger("shippingDeliveryWindow")
              }
              min={1}
            />
          </div>

          <div className={`${styles.bottom}`}>
            {isEditMode ? (
              <primaryComponents.Button
                onClick={handleSubmit(handleEditShipping)}
                isLoading={isEditingShipping}
                isDisabled={isEditingShipping}
                classNames="btn btn--primary cover"
                loadingText={"Updating..."}
              >
                Update shipping group
              </primaryComponents.Button>
            ) : (
              <>
                <div></div>
                <primaryComponents.Button
                  onClick={handleSubmit(handleAddShipping)}
                  isLoading={isAddingShipping}
                  isDisabled={isAddingShipping}
                  classNames="btn btn--primary"
                  loadingText={"Adding..."}
                >
                  Create
                </primaryComponents.Button>
              </>
            )}
          </div>
        </div>
      </div>
      <Modal
        isActive={addCityModal !== null}
        width={500}
        closeModal={() => setAddCityModal(null)}
      >
        <UpdateCityModal
          country_id={selectedCountry}
          state_id={addCityModal}
          closeModal={() => setAddCityModal(null)}
        />
      </Modal>
    </div>
  );
};

export default CreateShippingForm;
